import type { ConferenceParticipant, ConferenceState, ParticipantState } from './types';

const LIVE_LEG_STATES: ReadonlySet<ParticipantState> = new Set([
  'DIALING',
  'RINGING',
  'JOINED',
  'ON_HOLD',
]);

/**
 * Is this room really over?
 *
 * Not just its status. The service can mark a room FAILED because a losing
 * fork of the host's leg dropped while the real legs are still connected, so a
 * terminal status only counts once nobody is left dialling, ringing or talking.
 * Everything that reacts to a room ending — polling, the ended screen, hanging
 * up our own leg — has to agree on this, or one of them cuts off a live call.
 */
export function isConferenceOver(state: ConferenceState | undefined): boolean {
  if (!state) return false;
  const { status } = state.conference;
  if (status !== 'ENDED' && status !== 'FAILED') return false;
  return !state.participants.some((p) => LIVE_LEG_STATES.has(p.state));
}

const CONNECTED_LEG_STATES: ReadonlySet<ParticipantState> = new Set(['JOINED', 'ON_HOLD']);

/**
 * Is the call actually up — someone in the room, not just ringing?
 *
 * The counterpart of `isConferenceOver`: a room the service has marked FAILED
 * can still have people talking in it, and that call should read as live with
 * its timer running, not as "Connecting…".
 */
export function isConferenceConnected(state: ConferenceState | undefined): boolean {
  if (!state) return false;
  if (state.conference.status === 'ACTIVE') return true;
  return state.participants.some((p) => CONNECTED_LEG_STATES.has(p.state));
}

/** Why a live call has nobody left for the host to talk to. */
export type CallWrapUp =
  | { reason: 'unreachable'; legs: ConferenceParticipant[] }
  | { reason: 'left'; legs: ConferenceParticipant[] };

/**
 * Should this live call wrap itself up?
 *
 * Yes when everyone we dialled failed to connect — their devices were offline,
 * declined, or never answered — so the host isn't left alone in a silent room.
 * And in a one-to-one call, yes once the other person has hung up, the way any
 * phone call ends. A group call stays up when someone leaves: the host may
 * still be talking to others, or pull someone new in.
 *
 * "Everyone failed" only counts once everyone invited has been dialled. The
 * service rings a group's members one after another, so the legs on hand can
 * all be gone while the next person's leg does not exist yet — `invitedUserIds`
 * is who we asked it to ring. A group whose invite list isn't known never
 * wraps up on its own; a one-to-one has only one person to wait for.
 */
export function callWrapUp(
  state: ConferenceState | undefined,
  invitedUserIds: readonly string[] = [],
): CallWrapUp | null {
  if (!state || state.conference.status !== 'ACTIVE') return null;
  const others = state.participants.filter((p) => p.role !== 'HOST');
  if (others.length === 0) return null;
  if (others.some((p) => LIVE_LEG_STATES.has(p.state))) return null;
  if (others.every((p) => p.state === 'FAILED')) {
    const invited = invitedUserIds.filter((id) => id !== state.conference.hostUserId);
    if (invited.length === 0 && state.conference.type !== 'INTERNAL_1_1') return null;
    const dialled = new Set(others.map((p) => p.userId));
    if (invited.some((id) => !dialled.has(id))) return null;
    return { reason: 'unreachable', legs: others };
  }
  if (state.conference.type === 'INTERNAL_1_1') {
    return { reason: 'left', legs: others.filter((p) => p.state === 'LEFT') };
  }
  return null;
}
